import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Menu, X } from 'lucide-react';
import { FrogLogo } from './FrogLogo';
interface HeaderProps {
  forceSolid?: boolean;
  onLogoClick?: () => void;
}
export function Header({
  forceSolid = false,
  onLogoClick
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(forceSolid);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    if (forceSolid) {
      setIsScrolled(true);
      return;
    }
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [forceSolid]);
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onLogoClick) onLogoClick();
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`} data-id="element-166">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-167">
        <div className="flex items-center" data-id="element-168">
          {/* Logo — fixed width so nav centers properly */}
          <a href="#" onClick={handleLogoClick} className="flex items-center gap-2 group shrink-0" data-id="element-169">
            <FrogLogo className="w-10 h-10 transition-transform duration-300 group-hover:scale-105" data-id="element-170" />
            <span className={`text-2xl font-italiana font-bold tracking-wider transition-colors duration-300 ${isScrolled ? 'text-luxury-slate' : 'text-white'}`} data-id="element-171">
              Luxury Pads
            </span>
          </a>

          {/* Desktop Navigation — centered in remaining space */}
          <nav className="hidden md:flex items-center justify-center flex-1 gap-10" data-id="element-172">
            <a href="#destinations" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-luxury-blue' : 'text-white/90 hover:text-white'}`} data-id="element-173">
              Destinations
            </a>
            <a href="#properties" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-luxury-blue' : 'text-white/90 hover:text-white'}`} data-id="element-174">
              Property Types
            </a>
            <a href="#about" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-luxury-blue' : 'text-white/90 hover:text-white'}`} data-id="element-175">
              About
            </a>
          </nav>

          {/* Desktop Actions — fixed width right side */}
          <div className="hidden md:flex items-center gap-4 shrink-0" data-id="element-176">
            <button className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-luxury-blue' : 'text-white/90 hover:text-white'}`} data-id="element-177">
              <Globe className="w-4 h-4" data-id="element-178" />
              <span data-id="element-179">EN | £</span>
            </button>
            <div className="flex items-center gap-2" data-id="element-180">
              <button className={`text-sm font-medium px-5 py-2 rounded-full border transition-colors ${isScrolled ? 'border-gray-300 text-gray-700 hover:border-luxury-blue hover:text-luxury-blue' : 'border-white/60 text-white hover:border-white hover:bg-white/10'}`} data-id="element-181">
                Register
              </button>
              <button className={`text-sm font-medium px-5 py-2 rounded-full transition-colors ${isScrolled ? 'bg-luxury-slate text-white hover:bg-luxury-slate/90' : 'bg-white text-luxury-slate hover:bg-white/90'}`} data-id="element-182">
                Sign In
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button className={`md:hidden p-2 transition-colors ${isScrolled ? 'text-gray-700' : 'text-white'}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-183">
            {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-184" /> : <Menu className="w-6 h-6" data-id="element-185" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-186">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-t border-gray-100 overflow-hidden" data-id="element-187">
            <div className="px-4 pt-2 pb-6 space-y-4 shadow-lg" data-id="element-188">
              <a href="#destinations" className="block px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-md" data-id="element-189">
                Destinations
              </a>
              <a href="#properties" className="block px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-md" data-id="element-190">
                Property Types
              </a>
              <a href="#about" className="block px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-md" data-id="element-191">
                About
              </a>
              <div className="border-t border-gray-100 pt-4 flex flex-col space-y-3" data-id="element-192">
                <button className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-md" data-id="element-193">
                  <Globe className="w-5 h-5" data-id="element-194" />
                  <span data-id="element-195">EN | £</span>
                </button>
                <button className="w-full text-left px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-md" data-id="element-196">
                  Register
                </button>
                <button className="w-full text-center px-4 py-3 text-base font-medium rounded-lg border border-luxury-slate text-luxury-slate hover:bg-luxury-slate hover:text-white transition-colors" data-id="element-197">
                  Sign In
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}